import React, { useEffect, useState } from "react";
import Select from "react-select";
import {
  API_GET_ALL_PROJECTS_PROJECTID_AND_PROJECTNAME,
  API_GET_SHADOW_RESOURCE_PROJECT_DETAILS_BY_PROJECT_ID,
  API_GET_PROJECTS_BY_EMPLOYEE_ID,
} from "../../../config/Endpoints";
import { useEmployeeStore } from "../../../stores/employeeListStore";
import { getApi } from "../../../utils/api";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { projectTypeOptions } from "../../../options/projectTypesOptions";
import { displayFormatDate } from "../../../utils/helpers";
import { MdKeyboardArrowDown, MdOutlineKeyboardArrowUp } from "react-icons/md";
import CSATTeamMembersTable from "./CSATTeamMembersTable";
import { ThreeDots } from "react-loader-spinner";
import NoRecordsAvailable from "../../NoRecordsAvailable";

const CSATRatingTab = () => {
  const [projects, setProjects] = useState([]);
  const [employeeProjects, setEmployeeProjects] = useState([]);
  const [openProjectId, setOpenProjectId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [projectDetailsLoading, setProjectDetailsLoading] = useState(false);
  const [selectedFilterType, setSelectedFilterType] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [projectDetails, setProjectDetails] = useState(null);
  const { employeeList, fetchEmployees } = useEmployeeStore();
  const [employeeLoading, setEmployeeLoading] = useState(false);
  const accessToken = Cookies.get("accessToken");

  const filterOptions = [
    { value: "project", label: "Project Name" },
    { value: "employee", label: "Employee Name" },
  ];

  const getProjectTypeLabel = (value) => {
    const option = projectTypeOptions.find((opt) => opt.value === value);
    return option ? option.label : projectDetails?.projectType;
  };

  // Fetch projects and employees
  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      try {
        const res = await getApi(
          API_GET_ALL_PROJECTS_PROJECTID_AND_PROJECTNAME,
          accessToken
        );
        const list = res.data?.data || [];
        const options = list.map((p) => ({
          value: p.projectId,
          label: p.projectName,
        }));
        setProjects(options);
      } catch (err) {
        toast.error(err.message || "Error fetching project details");
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
    fetchEmployees();
  }, [fetchEmployees]);

  useEffect(() => {
    const fetchEmployeeProjects = async () => {
      if (selectedFilterType?.value === "employee" && selectedOption) {
        setEmployeeProjects([]);
        setOpenProjectId(null);
        setEmployeeLoading(true);
        try {
          const res = await getApi(
            `${API_GET_PROJECTS_BY_EMPLOYEE_ID}/${selectedOption.value}`,
            accessToken
          );

          if (res.data.statusCode === 404) {
            setEmployeeProjects([]);
            return;
          }

          if (res.data.statusCode === 200) {
            const projectsList = res.data?.data?.projects || [];
            setEmployeeProjects(projectsList);
          }
        } catch (err) {
          if (err.response?.status !== 404) {
            toast.error(err.message || "Error fetching employee projects");
          }
          setEmployeeProjects([]);
        } finally {
          setEmployeeLoading(false);
        }
      } else {
        setEmployeeProjects([]);
        setOpenProjectId(null);
      }
    };

    fetchEmployeeProjects();
  }, [selectedOption, selectedFilterType]);

  const toggleProject = (id) =>
    setOpenProjectId(openProjectId === id ? null : id);

  const secondFilterOptions =
    selectedFilterType?.value === "project" ? projects : employeeList;

  useEffect(() => {
    const fetchProjectDetails = async () => {
      if (selectedFilterType?.value === "project" && selectedOption) {
        setProjectDetailsLoading(true);
        try {
          const res = await getApi(
            `${API_GET_SHADOW_RESOURCE_PROJECT_DETAILS_BY_PROJECT_ID}/${selectedOption.value}`,
            accessToken
          );
          if (res.data.statusCode === 200) {
            setProjectDetails(res.data.data);
          } else {
            toast.error(res.data.message || "Failed to fetch project details");
          }
        } catch (err) {
          toast.error("Error fetching project details");
        } finally {
          setProjectDetailsLoading(false);
        }
      } else {
        setProjectDetails(null);
      }
    };

    fetchProjectDetails();
  }, [selectedOption, selectedFilterType]);

  return (
    <div className="row">
      <div className="card csat-rating-filters-card rounded-lg">
        <div className=" row col-md-12 g-3 justify-content-center">
          {/* Filter By Dropdown */}
          <div className="col-md-5">
            <Select
              placeholder="Filter By"
              options={filterOptions}
              value={selectedFilterType}
              onChange={(val) => {
                setSelectedFilterType(val);
                setSelectedOption(null);
                setProjectDetails(null);
              }}
              isClearable
            />
          </div>

          <div className="col-md-5">
            <Select
              placeholder={
                selectedFilterType?.label
                  ? `Select ${selectedFilterType.label}`
                  : "Select Option"
              }
              options={secondFilterOptions}
              value={selectedOption}
              onChange={setSelectedOption}
              isDisabled={!selectedFilterType}
              isClearable
            />
          </div>
        </div>
      </div>

      {projectDetailsLoading ? (
        <div className="d-flex justify-content-center mt-4">
          <ThreeDots
            height="80"
            width="80"
            radius="9"
            color="blue"
            ariaLabel="three-dots-loading"
          />
        </div>
      ) : (
        projectDetails && (
          <div className="mt-4 px-3">
            <div className="row">
              <div className="col-md-12">
                <div className="row mb-2 g-0 d-flex align-items-stretch">
                  <div className="col-md-6 px-4 mt-2 d-flex flex-column project-details">
                    <div className="row p-2 align-items-center">
                      <label className="col-md-4 text-black-50 mb-2">
                        Project Name
                      </label>
                      <div className="col-md-8 px-4 py-2 rounded-2 project-details-field ">
                        {projectDetails.projectName}
                      </div>
                    </div>

                    <div className="row p-2 align-items-center">
                      <label className="col-md-4 text-black-50 mb-2">
                        Project Type
                      </label>
                      <div className="col-md-8 px-4 py-2 rounded-2 project-details-field ">
                        {getProjectTypeLabel(projectDetails.projectType)}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6 px-4 mt-2 d-flex flex-column project-details">
                    {/* Delivery Lead */}
                    <div className="row p-2 align-items-center">
                      <label className="col-md-4 text-black-50 mb-2">
                        Delivery Lead
                      </label>
                      <div className="col-md-8 px-4 py-2 rounded-2 project-details-field ">
                        {projectDetails.dlName || "N/A"}
                      </div>
                    </div>
                    {/* SOW Dates */}
                    <div className="row p-2 align-items-center">
                      <label className="col-md-4 text-black-50 mb-2">SOW</label>
                      <div className="col-md-8 d-flex gap-2 p-0">
                        <div className="position-relative project-date-box">
                          <span className="m-0 dateBadgeStyle">Start Date</span>
                          <div className="py-3 px-2 rounded-2 text-center dateBoxStyle">
                            {projectDetails.sowStartDate
                              ? displayFormatDate(projectDetails.sowStartDate)
                              : "Ongoing"}
                          </div>
                        </div>
                        <div className="position-relative project-date-box">
                          <span className="m-0 dateBadgeStyle">End Date</span>
                          <div className="py-3 px-2 rounded-2 text-center dateBoxStyle">
                            {projectDetails.sowEndDate
                              ? displayFormatDate(projectDetails.sowEndDate)
                              : "Ongoing"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="mt-4">
                      <CSATTeamMembersTable
                        projectId={selectedOption?.value}
                        membersData={[
                          ...(projectDetails?.shadowResources || []),
                          // Add PM
                          ...(projectDetails?.pmName && projectDetails?.pmId
                            ? [
                                {
                                  employeeId: projectDetails.pmId,
                                  employeeName: projectDetails.pmName,
                                  fullName: projectDetails.pmName,
                                  designation: "project_manager",
                                  joiningDate: projectDetails.sowStartDate,
                                  status: 1,
                                  isProjectRole: true,
                                },
                              ]
                            : []),
                          // Add DL
                          ...(projectDetails?.dlName && projectDetails?.dlId
                            ? [
                                {
                                  employeeId: projectDetails.dlId,
                                  employeeName: projectDetails.dlName,
                                  fullName: projectDetails.dlName,
                                  designation: "delivery_lead",
                                  joiningDate: projectDetails.sowStartDate,
                                  status: 1,
                                  isProjectRole: true,
                                },
                              ]
                            : []),
                          // Add PS
                          ...(projectDetails?.psName && projectDetails?.psId
                            ? [
                                {
                                  employeeId: projectDetails.psId,
                                  employeeName: projectDetails.psName,
                                  fullName: projectDetails.psName,
                                  designation: "principal_sponsor",
                                  joiningDate: projectDetails.sowStartDate,
                                  status: 1,
                                  isProjectRole: true,
                                },
                              ]
                            : []),
                          // Add LBA
                          ...(projectDetails?.lbaName && projectDetails?.lbaId
                            ? [
                                {
                                  employeeId: projectDetails.lbaId,
                                  employeeName: projectDetails.lbaName,
                                  fullName: projectDetails.lbaName,
                                  designation: "lead_business_analyst",
                                  joiningDate: projectDetails.sowStartDate,
                                  status: 1,
                                  isProjectRole: true,
                                },
                              ]
                            : []),
                        ]}
                        shouldFetchMembers={true}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      )}
      {selectedFilterType?.value === "employee" &&
        employeeProjects.length > 0 && (
          <div className="accordion mt-4" id="employeeProjectsAccordion">
            {employeeProjects.map((proj) => (
              <div
                className="accordion-item project-accordion-item"
                key={proj.projectId}
              >
                <h2
                  className="accordion-header"
                  id={`heading-${proj.projectId}`}
                >
                  <button
                    className={`accordion-button ${
                      openProjectId === proj.projectId ? "" : "collapsed"
                    }`}
                    type="button"
                    onClick={() => toggleProject(proj.projectId)}
                  >
                    <span>{proj.projectName}</span>

                    <span
                      className={`project-status-badge ${
                        proj.status === 1
                          ? "badge-complete"
                          : proj.status === 2
                          ? "badge-onhold"
                          : "badge-active"
                      }`}
                    >
                      {proj.status === 1
                        ? "Completed"
                        : proj.status === 2
                        ? "On Hold"
                        : "Active"}
                    </span>

                    {openProjectId === proj.projectId ? (
                      <MdOutlineKeyboardArrowUp className="dropdown-icon" />
                    ) : (
                      <MdKeyboardArrowDown className="dropdown-icon" />
                    )}
                  </button>
                </h2>

                <div
                  id={`collapse-${proj.projectId}`}
                  className={`accordion-collapse collapse ${
                    openProjectId === proj.projectId ? "show" : ""
                  }`}
                  data-bs-parent="#employeeProjectsAccordion"
                >
                  <div className="accordion-body">
                    <div className="row">
                      <div className="col-md-12">
                        <div className="row mb-2 g-0 d-flex ">
                          <div className="col-md-6 px-4 mt-2  d-flex flex-column project-details">
                            <div className="row p-2 mt-2 align-items-center  ">
                              <label className="col-md-5  text-black-50 mb-2 ">
                                Project Name
                              </label>
                              <div
                                className="col-md-7 px-4 py-2 rounded-2 project-details-field"
                                style={{
                                  wordBreak: "break-word",
                                  overflowWrap: "anywhere",
                                  whiteSpace: "normal",
                                }}
                              >
                                {proj.projectName}
                              </div>
                            </div>
                            <div className="row p-2 align-items-center">
                              <label className="col-md-5  text-black-50 mb-2">
                                Project Type
                              </label>
                              <div className="col-md-7 px-4 py-2 rounded-2 project-details-field ">
                                {getProjectTypeLabel(proj?.projectType)}
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6 px-4 mt-2  d-flex flex-column project-details">
                            <div className="row p-2 align-items-center">
                              <label className="col-md-5  text-black-50 mb-2">
                                Delivery Lead
                              </label>
                              <div className="col-md-7 px-4 py-2 rounded-2 project-details-field ">
                                {proj.dlName ? proj.dlName : "N/A"}
                              </div>
                            </div>
                            <div className="row p-2 align-items-center">
                              <label className="col-md-5 text-black-50 mb-2">
                                SOW
                              </label>
                              <div className="col-md-7 d-flex gap-2 p-0">
                                <div className="position-relative project-date-box">
                                  <span className="m-0 dateBadgeStyle">
                                    Start Date
                                  </span>
                                  <div className="py-3 px-2 rounded-2 text-center dateBoxStyle">
                                    {proj.sowStartDate
                                      ? displayFormatDate(proj.sowStartDate)
                                      : "Ongoing"}
                                  </div>
                                </div>
                                <div className="position-relative project-date-box">
                                  <span className="m-0 dateBadgeStyle">
                                    End Date
                                  </span>
                                  <div className="py-3 px-2 rounded-2 text-center dateBoxStyle">
                                    {proj.sowEndDate
                                      ? displayFormatDate(proj.sowEndDate)
                                      : "Ongoing"}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="mt-4">
                        <CSATTeamMembersTable
                          projectId={proj.projectId}
                          membersData={[
                            ...(proj.members || []),
                            ...(proj.shadowMembers || []),

                            ...(proj?.projectManagerName &&
                            proj?.projectManagerId
                              ? [
                                  {
                                    employeeId: proj.projectManagerId,
                                    employeeName: proj.projectManagerName,
                                    fullName: proj.projectManagerName,
                                    designation: "project_manager",
                                    joiningDate: proj.sowStartDate,
                                    status: 1,
                                    isProjectRole: true,
                                  },
                                ]
                              : []),
                            // Add DL - only for THIS project
                            ...(proj?.dlName && proj?.dlId
                              ? [
                                  {
                                    employeeId: proj.dlId,
                                    employeeName: proj.dlName,
                                    fullName: proj.dlName,
                                    designation: "delivery_lead",
                                    joiningDate: proj.sowStartDate,
                                    status: 1,
                                    isProjectRole: true,
                                  },
                                ]
                              : []),

                            ...(proj?.principalSponsorName &&
                            proj?.principalSponsorId
                              ? [
                                  {
                                    employeeId: proj.principalSponsorId,
                                    employeeName: proj.principalSponsorName,
                                    fullName: proj.principalSponsorName,
                                    designation: "principal_sponsor",
                                    joiningDate: proj.sowStartDate,
                                    status: 1,
                                    isProjectRole: true,
                                  },
                                ]
                              : []),

                            ...(proj?.leadBusinessAnalystName &&
                            proj?.leadBusinessAnalyst
                              ? [
                                  {
                                    employeeId: proj.leadBusinessAnalyst,
                                    employeeName: proj.leadBusinessAnalystName,
                                    fullName: proj.leadBusinessAnalystName,
                                    designation: "lead_business_analyst",
                                    joiningDate: proj.sowStartDate,
                                    status: 1,
                                    isProjectRole: true,
                                  },
                                ]
                              : []),
                          ]}
                          shouldFetchMembers={false}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

      {/* No projects found */}
      {selectedFilterType?.value === "employee" &&
        selectedOption &&
        (employeeLoading ? (
          <div className="d-flex justify-content-center mt-4">
            <ThreeDots
              height="80"
              width="80"
              radius="9"
              color="blue"
              ariaLabel="three-dots-loading"
            />
          </div>
        ) : (
          employeeProjects.length === 0 && <NoRecordsAvailable />
        ))}
    </div>
  );
};

export default CSATRatingTab;
